import { Badge, Button, Card, CardContent, Input, Select, Spinner } from '@crm/ui';
import { ChevronRight, Clock, Pencil, Plus, Scissors, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Autocomplete,
  type AutocompleteOption,
  ConfirmDialog,
  EmptyState,
  FilterBar,
  PageHeader,
  Pagination,
} from '@/components';
import { useDebounce, useDocumentTitle } from '@/hooks';
import { formatCurrency } from '@/lib/utils';
import { useCan, useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { CATALOG_ROUTES, useCategories, useDeleteService, useServices } from '../api';

export function ServicesPage() {
  useDocumentTitle('Servicos');

  const canWrite = useCan('services', 'WRITE');
  const { user } = useSession();
  const units = user?.units ?? [];

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [selectedUnits, setSelectedUnits] = useState<AutocompleteOption[]>([]);
  const debouncedSearch = useDebounce(search, 300);

  const categories = useCategories({ page: 1 });
  const services = useServices({
    page,
    perPage,
    search: debouncedSearch,
    categoryId: categoryId || undefined,
    unitId: selectedUnits[0]?.id,
  });
  const deleteService = useDeleteService();

  const [pendingRemoval, setPendingRemoval] = useState<{ id: string; name: string } | null>(null);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Scissors />}
        title="Servicos"
        description="Catalogo com ficha tecnica: preco, duracao e limites de desconto."
        actions={
          canWrite ? (
            <Button asChild>
              <Link to={CATALOG_ROUTES.SERVICE_NEW}>
                <Plus className="h-4 w-4" />
                <span>
                  Novo<span className="hidden sm:inline"> servico</span>
                </span>
              </Link>
            </Button>
          ) : null
        }
      />

      {/* No celular fica so a busca com o botao ao lado: unidade e categoria
          empurravam o catalogo para fora da dobra. */}

      {deleteService.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(deleteService.error)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent className="space-y-6">
          <FilterBar
            className="sm:flex sm:flex-row sm:flex-wrap sm:items-center"
            activeCount={selectedUnits.length + (categoryId ? 1 : 0)}
            search={
              <Input
                className="sm:max-w-xs"
                placeholder="Buscar por nome"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            }
          >
            {/* Unidade primeiro, como em toda tela do sistema. */}
            {units.length > 1 ? (
              <Autocomplete
                className="sm:order-first sm:w-52"
                multiple
                emptyLabel="Todas as unidades"
                placeholder="Filtrar unidade"
                url="/api/v1/units"
                value={selectedUnits}
                onChange={(chosen) => {
                  setSelectedUnits(chosen);
                  setPage(1);
                }}
              />
            ) : null}

            <Select
              className="sm:max-w-48"
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Todas as categorias</option>
              {(categories.data?.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </FilterBar>

          {services.isLoading ? (
            <div className="flex justify-center py-8">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : services.isError ? (
            <p className="text-sm text-destructive">{toErrorMessage(services.error)}</p>
          ) : services.data && services.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {services.data.data.map((service) => (
                <li
                  key={service.id}
                  // Acoes a direita, na mesma linha: empilhadas, cada servico
                  // gastava duas alturas na tela do celular.
                  className="flex items-center gap-2 py-3 first:pt-0 last:pb-0"
                >
                  {/* A linha toda abre a ficha do servico: no celular o alvo
                      e o registro inteiro, nao um icone no canto. */}
                  <Link
                    to={CATALOG_ROUTES.service(service.id)}
                    className="flex min-w-0 flex-1 items-center gap-3"
                  >
                    {service.images[0] ? (
                      <img
                        src={service.images[0]}
                        alt={service.name}
                        loading="lazy"
                        className="h-12 w-12 shrink-0 rounded-md border border-border object-cover"
                      />
                    ) : (
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md border border-dashed border-border">
                        <Scissors className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}

                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{service.name}</span>
                        {service.categoryName ? (
                          <Badge variant="secondary">{service.categoryName}</Badge>
                        ) : null}
                        {/* Unidade e limite de desconto sao detalhes de quem
                            edita o servico: no celular eles empurravam preco e
                            duracao para uma quarta linha. */}
                        {service.unitName ? (
                          <Badge variant="outline" className="hidden sm:inline-flex">
                            {service.unitName}
                          </Badge>
                        ) : null}
                        {service.isActive ? null : <Badge variant="destructive">Inativo</Badge>}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">
                          {formatCurrency(service.price)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {service.durationMinutes} min
                        </span>
                        {service.maxDiscountPercent > 0 ? (
                          <span className="hidden sm:inline">
                            desconto ate {service.maxDiscountPercent}%
                          </span>
                        ) : null}
                        {service.autoDiscountPercent > 0 ? (
                          <span className="text-emerald-600 dark:text-emerald-400">
                            promo {service.autoDiscountPercent}%
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </Link>

                  <ChevronRight
                    aria-hidden
                    className="h-5 w-5 shrink-0 text-muted-foreground sm:hidden"
                  />

                  {canWrite ? (
                    <div className="hidden shrink-0 items-center sm:flex">
                      <Button variant="ghost" size="icon" title="Editar" asChild>
                        <Link to={CATALOG_ROUTES.service(service.id)}>
                          <Pencil className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Excluir"
                        disabled={deleteService.isPending}
                        onClick={() => setPendingRemoval({ id: service.id, name: service.name })}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Scissors />} title="Nenhum servico encontrado" />
          )}
        </CardContent>
      </Card>

      <Pagination
        meta={services.data?.meta}
        label="servicos"
        onChange={setPage}
        perPage={perPage}
        onPerPageChange={(value) => {
          setPerPage(value);
          setPage(1);
        }}
      />

      <ConfirmDialog
        open={pendingRemoval !== null}
        title="Excluir servico"
        message={`O servico ${pendingRemoval?.name ?? ''} sai do catalogo. Esta acao nao pode ser desfeita.`}
        loading={deleteService.isPending}
        onClose={() => setPendingRemoval(null)}
        onConfirm={() => {
          if (pendingRemoval) deleteService.mutate(pendingRemoval.id);
          setPendingRemoval(null);
        }}
      />
    </div>
  );
}
